/**
 * Unified font spec → CSS font-family name resolution.
 *
 * Composes Adobe and Google font utilities so that a single function can resolve
 * any branding font spec to the name used in CSS font-family. Keeps
 * adobe-fonts and google-fonts independent; this module is the only place that
 * needs to know about both.
 */

import {
  isAdobeFontSpec,
  getAdobeFontFamilyName,
  parseAdobeFontSpec,
} from "./adobe-fonts";
import { parseFontSpec, buildGoogleFontsUrl } from "./google-fonts";

/**
 * Returns the CSS font-family value for a branding font spec.
 *
 * - Adobe specs (adobe:projectId:fontId) → fontId (e.g. "source-sans-pro")
 * - Google specs (name or name|weights) → name (e.g. "Poppins")
 * - Invalid or empty Adobe spec → empty string
 */
export function getFontFamilyName(spec: string): string {
  if (isAdobeFontSpec(spec)) {
    return getAdobeFontFamilyName(spec) ?? "";
  }
  return parseFontSpec(spec).name;
}

/**
 * Returns stylesheet link data for all branding fonts (heading + body + extras).
 * Keeps BrandedChakraProvider provider-agnostic: it only consumes this result.
 */
export function getBrandingFontLinks(
  headingFontSpec: string | undefined,
  bodyFontSpec: string | undefined,
  extraFontNames?: string[]
): { googleFontUrl: string | null; adobeProjectIds: string[] } {
  const googleSpecs: string[] = [];
  const adobeProjectIds = new Set<string>();
  const seen = new Set<string>();

  const addSpec = (spec: string | undefined): void => {
    if (!spec) return;
    if (isAdobeFontSpec(spec)) {
      const adobe = parseAdobeFontSpec(spec);
      if (adobe) adobeProjectIds.add(adobe.projectId);
    } else {
      const { name } = parseFontSpec(spec);
      if (!seen.has(name)) {
        seen.add(name);
        googleSpecs.push(spec);
      }
    }
  };

  addSpec(headingFontSpec);
  addSpec(bodyFontSpec);
  extraFontNames?.forEach((name) => addSpec(name));

  return {
    googleFontUrl: buildGoogleFontsUrl(googleSpecs),
    adobeProjectIds: [...adobeProjectIds],
  };
}
